import { animated, useTransition } from "@react-spring/web";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import pluralize from "pluralize";

import userRoleService from "src/utils/userRoles.service";

import { ScoreCardsContext } from "../context";

import Actions from "src/components/actionbar";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import Toolbar from "src/components/toolbar";
import Text from "src/components/text";
import Slider from "src/components/slider";
import { Input } from "src/components/input";

import { AssessmentConfiguration, AssessmentParameter } from "src/types/coaching/assessmentConfiguration";
import { createAssessmentConfiguration, deleteAssessmentConfiguration } from "src/redux/actions/coaching/settings";

import _ from "lodash";
import cx from "classnames";
import styles from "./edit.module.scss";

interface ScoreCardsMenuBarProps {
  show: boolean;
  list?: string | undefined;
  // eslint-disable-next-line no-unused-vars
  handleEditModeChange: (editMode: "create" | "rename" | "clone" | "delete" | null) => void;
}


export const ScoreCardsMenuBar: React.FC<ScoreCardsMenuBarProps> = ({
  show,
  handleEditModeChange,
  list
}) => {
  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

  useEffect(() => {
    api.start();
  }, [show]);

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.collectionsMenuBar)}>
          <Actions.Container>
            <Actions.Row>
              {userRoleService.canCreateCourse() && (
                <Actions.Button
                  width={4}
                  icon={"paperflite-add"}
                  title="create new framework"
                  onClick={() => handleEditModeChange("create")}
                />
              )}
            </Actions.Row>
          </Actions.Container>
        </animated.div>
      ),
  );
};

interface RenameMenuBarProps {
  show: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RenameMenubar: React.FC<RenameMenuBarProps> = ({
  show,
  onConfirm,
  onCancel,
}) => {
  const { edits } = React.useContext(ScoreCardsContext);

  const renaming = useSelector((state: any) => {
    const updates = _.keys(state.coaching.settings.scorecards.update).filter((key) => {
      const item = state.coaching.settings.scorecards.update[key];
      return item.updating;
    });

    return _.size(updates) > 0;
  });

  const renameRequests = useSelector((state: any) => {
    const scorecards = _.get(state, 'coaching.settings.scorecards.items') || [];
    
    const requests = edits.filter((edit) => {
      return edit.value !== null;
    }).map((edit) => {
      const scorecard = scorecards?.find((item: AssessmentConfiguration) => item.id === edit.id); 
      if(!scorecard) {
        return null;
      }

      const oldName = scorecard.name;
      const newName = edit.value;

      if(oldName === newName) {
        return null;
      }

      return {
        id: edit.id,
        oldName: scorecard.name,
        newName: edit.value,
      };
    }).filter((r) => r !== null);

    return requests;
  }, _.isEqual);

  useEffect(() => {
    if (!renaming && show) {
      onCancel();
    }
  }, [renaming]);

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Reveal show={show} className={styles.renameConfirmation}>
      <div className={styles.message}>
        All the changes made to this section will reflect for all users in the create simulation.
      </div>

      <Button
        variant="primary"
        icon="paperflite-tick"
        disabled={renaming || _.size(renameRequests) === 0}
        text={renaming ? 'Saving...' : 'Save'}
        onClick={handleConfirm}
      />

      <Button
        variant="secondary"
        text="Cancel"
        disabled={renaming}
        onClick={onCancel}
      />
    </Reveal>
  );
};


interface CloneMenubarProps {
  show: boolean;
  onCancel: () => void;
}

export const CloneMenubar: React.FC<CloneMenubarProps> = ({
  show,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { editMode, edits, onEditChanges } = React.useContext(ScoreCardsContext);
  const [cloneScoreCard, setCloneScoreCard] = React.useState<AssessmentConfiguration | null>(null);

  useEffect(() => {
    if (editMode !== 'clone') return;

    const scorecards = edits.filter(
      (edit) => edit.value !== null
    );

    if (scorecards.length > 0) {
      setCloneScoreCard(
        scorecards[0].value as AssessmentConfiguration
      );
    }
  }, [edits, editMode]);

  const handleCloneCancel = () => {
    if (cloneScoreCard) {
      onEditChanges({
        id: cloneScoreCard.id!,
        mode: 'clone',
        value: null,
      });
    }

    setCloneScoreCard(null);
  };

  const handleCloneFinish = () => {
    handleCloneCancel();
  };

  return (
    <>
      <Reveal show={show} className={styles.cloneConfirmation}>
        <div className={styles.message}>
          Select card to clone.
        </div>
        <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>
      </Reveal>
      {cloneScoreCard && (
        <CloneConfirmationDialog
          show={!!cloneScoreCard}
          scorecard={cloneScoreCard}
          onFinish={handleCloneFinish}
          onClose={handleCloneCancel}
        />
      )}
    </>
  );
};

interface CloneConfirmationDialogProps {
  show: boolean;
  scorecard: AssessmentConfiguration;
  // eslint-disable-next-line no-unused-vars
  onFinish: (scorecard: AssessmentConfiguration) => void;
  onClose: () => void;
}

export const CloneConfirmationDialog: React.FC<
  CloneConfirmationDialogProps
> = ({ show, scorecard, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const cloning = useSelector(
    (state) => _.get(state, `coaching.settings.scorecards.creation.creating`) || false,
  );

  const clonedScoreCard = useSelector((state) => {
    const finished = _.get(state, `coaching.settings.scorecards.creation.created`) || false;
    if (finished) {
      return _.get(state,`coaching.settings.scorecards.creation.item`);
    }

    return null;
  });

  const [name, setName] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (clonedScoreCard) {
      onFinish(clonedScoreCard);
    }
  }, [clonedScoreCard]);

  const validateForm = () => {
    if (_.isEmpty(name)) {
      return "Please provide a valid name";
    }

    if (_.size(name) > 200) {
      return "Scorecard name cannot be greater than 200 characters";
    }

    return null;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  };

  const handleConfirm = () => {
    const e = validateForm();
    if (e) {
      setError(e);
      return;
    }

    dispatch(
      createAssessmentConfiguration({
        configurationId: scorecard.id,
        name,
      })
    );
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConfirm();
  };

  return (
    <Slider.Dialog
      className={styles.cloneConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header title="cloning..." message="1 scorecard"></Slider.Header>
      <Slider.Body>
        <div className={styles.details}>
          <div className={styles.collection}>
            <EditScoreCard scorecard={scorecard}/>
          </div>

          <form className={styles.addListForm} onSubmit={handleSubmit}>
            <Input
              autoFocus
              className={styles.input}
              placeholder="add a scorecard name..."
              value={name}
              onChange={handleNameChange}
            />
            <Reveal show={!!error}>
              <div className={styles.error}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                {error}
              </div>
            </Reveal>
          </form>
          <p className={styles.description}>
          Clone away! Create an exact copy of this scorecard to tweak...
          </p>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={cloning ? "Cloning..." : "Confirm"}
          variant="primary"
          disabled={cloning}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={cloning}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface CloneHelpToolbarProps {
  show: boolean;
}

export const CloneHelpToolbar: React.FC<CloneHelpToolbarProps> = ({show}) => {
  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span className={styles.cloneCollectionHelper}>Pick a scorecard</span>
        </Toolbar.Text>
      </Toolbar.Container>
    </>
  );
};

interface DeleteMenubarProps {
  show: boolean;
  onCancel: () => void;
}

export const DeleteMenubar: React.FC<DeleteMenubarProps> = ({
  show,
  onCancel,
}) => {

  const deleting = false;
  const [confirm, setConfirm] = React.useState(false);

  const { edits, onEditModeChange } = React.useContext(ScoreCardsContext);
  const scorecards = edits
    .filter((edit) => edit.value)
    .map((edit) => edit.value as AssessmentConfiguration);

  const onConfirm = () => {
    if (_.size(scorecards) == 0) {
      return;
    }

    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onCancel();
  };

  const handleCancel = () => {
    setConfirm(false);
    onEditModeChange(null);
  };

  return (
    <Reveal show={show} className={styles.deleteConfirmation}>
      <div className={styles.message}>
        Click on one or more scorecards you wish to delete.
      </div>
      <Button
        variant="primary"
        icon="paperflite-delete"
        text={deleting ? "Deleting..." : "Delete"}
        disabled={_.size(scorecards) == 0}
        onClick={onConfirm}
      />
      <Button variant="secondary" text="Cancel" onClick={handleCancel}></Button>

      <DeleteConfirmationDialog
        show={confirm}
        scorecards={scorecards}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </Reveal>
  );
};

interface DeleteSelectionToolbarProps {
  show: boolean;
  scorecards: AssessmentConfiguration[];
  onFinish: () => void;
}

export const DeleteSelectionToolbar: React.FC<DeleteSelectionToolbarProps> = ({
  show,
  scorecards,
  onFinish,
}) => {
  const [confirm, setConfirm] = React.useState(false);

  const handleShowConfirm = () => {
    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onFinish();
  };

  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span>{_.size(scorecards)} Selected</span>
        </Toolbar.Text>
        <Toolbar.Divider />
        <Toolbar.IconButton
          icon="paperflite-delete"
          disabled={_.size(scorecards) == 0}
          onClick={handleShowConfirm}
        />
      </Toolbar.Container>
      <DeleteConfirmationDialog
        show={confirm}
        scorecards={scorecards}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </>
  );
};

interface DeleteConfirmationDialogProps {
  show: boolean;
  scorecards: AssessmentConfiguration[];
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({ show, scorecards, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const deleting = useSelector((state: any) => {
    const configuraitonDeleteions = _.keys(state.coaching.settings.scorecards.deletion).filter(
      (key) => {
        const item = state.coaching.settings.scorecards.deletion[key];
        return item.deleting;
      },
    );

    return _.size(configuraitonDeleteions) > 0;
  });

  const deleted = useSelector((state: any) => {
    const configuraitonDeleteions = _.keys(state.coaching.settings.scorecards.deletion).filter(
      (key) => {
        const item = state.coaching.settings.scorecards.deletion[key];
        return item.deleted;
      },
    );

    return _.size(configuraitonDeleteions) === scorecards.length;
  });

  const handleConfirm = () => {
    scorecards.forEach((scorecard) => {
      if (scorecard.id) {
        dispatch(deleteAssessmentConfiguration({ id: scorecard.id }));
      }
    });
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!deleting && deleted) {
      onFinish();
    }
  }, [deleting, deleted]);

  return (
    <Slider.Dialog
      className={styles.deleteConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header
         title={
          <>
            delete selected <br />
            scorecard(s)?
          </>
        }
        message={`${_.size(scorecards)} ${pluralize("scorecard", _.size(scorecards))}`}
      >
        <p className={styles.description} style={{ marginBottom: 10 }}>
        This action cannot be undone and the scorecard will no longer be accessible.
        </p>
      </Slider.Header>
      <Slider.Body>
        <div className={styles.grid}>
          {scorecards.map((scorecards, i) => {
            return <EditScoreCard key={i} scorecard={scorecards} />;
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          variant="primary"
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

const EditScoreCard = ({ scorecard }) => {

  const parameters = _.get(scorecard, 'parameters', []) as AssessmentParameter[];
  const parameterCount = _.size(parameters);

  let subParameterCount = 0;
  parameters.forEach(p => {
    subParameterCount += _.size(p.parameters);
  });

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.status}>
          {scorecard.published && (
            <div className={styles.detail}>
              <div className={styles.active}></div>
              <p>Active</p>
            </div>
          )}
          {!scorecard.published && (<div className={styles.detail}>
            <div className={styles.draft}></div>
            <p>Draft</p>
          </div>
          )}
        </div>
      </div>
      <div className={styles.information}>
        <div className={styles.name}>
          <Text text={scorecard.name} lines={1} />
        </div>
        <div className={styles.text}>
          <Text text={scorecard.description || ""} lines={2} tooltip={{placement:"auto"}} />
        </div>
        </div>
    </div>
  )
}